import { apiClient } from './api-client';
import { config } from '../config/config';

// Forms API Response Types
export interface DPForm {
    consentHeaderId: number;
    collectionPointId: number;
    collectionPointName: string;
    collectionPointType: 'API' | 'NOTICE';
    consentTimestamp: string;
    languageCode: string;
    consentSource: string;
    purposes: string[]; // Purpose names
    dataElements: string[]; // Data element names
}

export interface PurposeDetail {
    purposeId: number;
    purposeKey: string;
    purposeName: string;
    purposeDescription: string;
    originalConsent: boolean;
    currentConsent: boolean;
}

export interface DataElementDetail {
    dataElementId: number;
    dataElementKey: string;
    elementName: string;
    elementDescription: string;
    originalValue: string;
    currentValue: string;
    dataElementType: string;
    storageMode: 'DIRECT' | 'VAULT';
    vaultId: number | null;
    originalHash: string | null;
    currentStorageMode: 'DIRECT' | 'VAULT' | null;
    currentVaultId: number | null;
    currentHash: string | null;
    currentConsentHeaderId: number | null;
    valueChanged: boolean;
}

export interface FormDetails {
    consentHeaderId: number;
    collectionPointName: string;
    collectionPointType: 'API' | 'NOTICE';
    consentTimestamp: string;
    languageCode: string;
    purposes: PurposeDetail[];
    dataElements: DataElementDetail[];
}

export interface FormsResponse {
    success: boolean;
    forms: DPForm[];
}

export interface FormDetailsResponse {
    success: boolean;
    form: FormDetails;
}

// Forms Service Class
export class FormsService {
    private baseUrl: string;

    constructor() {
        this.baseUrl = config.api.endpoints.portal.getForms;
    }

    /**
     * Get all forms filled by the authenticated DP
     */
    async getForms(): Promise<DPForm[]> {
        try {
            const response = await apiClient.get<FormsResponse>(this.baseUrl);

            if (!response.success) {
                throw new Error('Failed to fetch forms');
            }

            return response.forms;
        } catch (error) {
            console.error('Error fetching forms:', error);
            throw error;
        }
    }

    /**
     * Get detailed form information including purposes and data elements
     */
    async getFormDetails(consentHeaderId: number): Promise<FormDetails> {
        try {
            const url = `${this.baseUrl}/${consentHeaderId}`;
            const response = await apiClient.get<FormDetailsResponse>(url);

            if (!response.success) {
                throw new Error('Failed to fetch form details');
            }

            return response.form;
        } catch (error) {
            console.error('Error fetching form details:', error);
            throw error;
        }
    }

    /**
     * Get forms with error handling and loading states
     */
    async getFormsWithState(): Promise<{
        data: DPForm[] | null;
        loading: boolean;
        error: string | null;
    }> {
        let data: DPForm[] | null = null;
        let error: string | null = null;
        let loading = true;

        try {
            data = await this.getForms();
        } catch (err: any) {
            error = err.message || 'Failed to fetch forms';
        } finally {
            loading = false;
        }

        return { data, loading, error };
    }

    /**
     * Get form details with error handling and loading states
     */
    async getFormDetailsWithState(consentHeaderId: number): Promise<{
        data: FormDetails | null;
        loading: boolean;
        error: string | null;
    }> {
        let data: FormDetails | null = null;
        let error: string | null = null;
        let loading = true;

        try {
            data = await this.getFormDetails(consentHeaderId);
        } catch (err: any) {
            error = err.message || 'Failed to fetch form details';
        } finally {
            loading = false;
        }

        return { data, loading, error };
    }

    /**
     * Format consent timestamp for display
     */
    formatConsentTimestamp(timestamp: string): string {
        try {
            const date = new Date(timestamp);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting timestamp:', error);
            return timestamp;
        }
    }

    /**
     * Get form type display name
     */
    getFormTypeDisplayName(type: 'API' | 'NOTICE'): string {
        switch (type) {
            case 'API':
                return 'Website Form';
            case 'NOTICE':
                return 'Portal Form';
            default:
                return type;
        }
    }

    /**
     * Check if consent is currently active
     */
    isConsentActive(originalConsent: boolean, currentConsent: boolean): boolean {
        return originalConsent && currentConsent;
    }

    /**
     * Get consent status display text
     */
    getConsentStatusText(originalConsent: boolean, currentConsent: boolean): string {
        if (originalConsent && currentConsent) {
            return 'Active';
        } else if (originalConsent && !currentConsent) {
            return 'Withdrawn';
        } else if (!originalConsent && currentConsent) {
            return 'Later Granted';
        } else {
            return 'Denied';
        }
    }
}

// Create and export a singleton instance
export const formsService = new FormsService();

// Export default
export default formsService;
